import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { useIntl } from 'react-intl';

import { AuthContextProvider } from '../contexts/auth-context.tsx';
import AuthGuard from '../guards/AuthGuard.tsx';
import ProtectedRoutes from './ProtectedRoutes.tsx';
import useProfile from '../hooks/useProfile.tsx';
import useAuth from '../hooks/useAuth.ts';
import { User } from '../types/user.ts';
import { isSdiAdmin } from '../utils/roles';
import { LocaleLayout } from '../components/layouts/LocaleLayout';
import { defaultLocale } from '../i18n';
import { localizePath, resolveOfflineLocale } from '../i18n/locale-path';

const UnProtectedRoutes = lazy(() => import('./UnProtectedRoutes.tsx'));
const Cgu = lazy(() => import("../pages/cgu/Cgu.tsx"));
const PrivacyPolicy = lazy(() => import("../pages/privacy-policy/PrivacyPolicy.tsx"));
const EquipmentLookup = lazy(() => import("../pages/equipment-lookup/EquipmentLookup.tsx"));
const EquipmentInfoPage = lazy(() => import("../pages/equipment-lookup/EquipmentInfoPage.tsx"));

function LegacyLocaleRedirect() {
  const location = useLocation();
  const locale = resolveOfflineLocale();
  return (
    <Navigate
      to={`/${locale}${location.pathname}${location.search}${location.hash}`}
      replace
    />
  );
}

function AppRoutes() {
  return (
    <HelmetProvider>
      <AuthContextProvider>
        <Routes>
          <Route path="/" element={<Navigate to={`/${defaultLocale}`} replace />} />

          {/* Legacy unprefixed paths → current locale */}
          <Route path="/auth/*" element={<LegacyLocaleRedirect />} />
          <Route path="/dashboard/*" element={<LegacyLocaleRedirect />} />
          <Route path="/equipment/*" element={<LegacyLocaleRedirect />} />
          <Route path="/cgu" element={<LegacyLocaleRedirect />} />
          <Route path="/privacy-policy" element={<LegacyLocaleRedirect />} />

          <Route path="/:locale" element={<LocaleLayout />}>
            <Route element={<RootLayout />}>
              <Route path="auth/*" Component={UnProtectedRoutes} />
              <Route path="dashboard/*" element={<AuthGuard><ProtectedRoutes /></AuthGuard>} />
              <Route index Component={EquipmentLookup} />
              <Route path="equipment/:id" Component={EquipmentInfoPage} />
              <Route path="cgu" Component={Cgu} />
              <Route path="privacy-policy" Component={PrivacyPolicy} />
              {/* Nested paths under an invalid "locale" still need a catch — unused for QR */}
              <Route path="*" element={<LocaleFallback />} />
            </Route>
          </Route>
        </Routes>
      </AuthContextProvider>
    </HelmetProvider>
  );
}

/** If somehow nested under a real locale with unknown path, go home for that locale */
function LocaleFallback() {
  const { locale } = useParams();
  return <Navigate to={localizePath(isLocale(locale) ? locale : defaultLocale, '/')} replace />;
}

function isLocale(value: string | undefined): value is 'fr' | 'en' | 'es' | 'pt' | 'nl' {
  return value === 'fr' || value === 'en' || value === 'es' || value === 'pt' || value === 'nl';
}

function RootLayout() {
  const { profile, getProfile } = useProfile();
  const { updateUser, user } = useAuth();
  const [displayMode, setDisplayMode] = useState(true);
  const intl = useIntl();

  const modeTheme = useMemo(() => {
    if (user) {
      return isSdiAdmin(user.role) ? "text-shadow bg-blue-300" : "text-white bg-blue-900";
    }
    return "text-white bg-[#0066B1]";
  }, [user]);

  useEffect(() => {
    if (user) {
      getProfile();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getProfile]);

  useEffect(() => {
    if (profile) {
      const { accountActivateAt, role, email, firstName, lastName, isPremium } = profile;
      const syncUser: User = {
        role,
        email,
        firstName,
        lastName,
        active: !!accountActivateAt,
        isPremium,
      };
      updateUser(syncUser);
    }
  }, [profile, updateUser]);

  return (
    <Suspense fallback={<Loading />}>
      {import.meta.env.MODE !== "production" && (
        <div
          className={`flex justify-between items-center transition-block duration-200 ease-linear ${modeTheme} ${displayMode ? "py-0.5 px-2.5" : "h-0 pointer-events-none"}`}
        >
          <p className="text-xs font-medium">
            {intl.formatMessage(
              { id: 'common.devMode' },
              { mode: import.meta.env.VITE_MODE }
            )}
          </p>
          {displayMode && (
            <button type="button" onClick={() => setDisplayMode(false)}>
              <svg fill="currentColor" width="25px" height="25px" viewBox="0 0 32 32" className="size-3.5">
                <path d="M19.587 16.001l6.096 6.096c0.396 0.396 0.396 1.039 0 1.435l-2.151 2.151c-0.396 0.396-1.038 0.396-1.435 0l-6.097-6.096-6.097 6.096c-0.396 0.396-1.038 0.396-1.434 0l-2.152-2.151c-0.396-0.396-0.396-1.038 0-1.435l6.097-6.096-6.097-6.097c-0.396-0.396-0.396-1.039 0-1.435l2.153-2.151c0.396-0.396 1.038-0.396 1.434 0l6.096 6.097 6.097-6.097c0.396-0.396 1.038-0.396 1.435 0l2.151 2.152c0.396 0.396 0.396 1.038 0 1.435l-6.096 6.096z"></path>
              </svg>
            </button>
          )}
        </div>
      )}
      <Outlet />
    </Suspense>
  );
}

function Loading() {
  const intl = useIntl();
  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-blue-gradient grid place-items-center">
      <p className="text-xl text-white">{intl.formatMessage({ id: 'common.loading' })}</p>
    </div>
  );
}

export default AppRoutes;
